import {
    AfterContentInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ContentChild, DestroyRef, inject
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
    MatCard, MatCardHeader, MatCardTitle
} from "@angular/material/card";
import { SubformComponent } from "../subform/subform.component";

import { CardContentDirective } from "../../../directives/card-content/card-content.directive";
import { FormSectionHeaderDescriptionDirective } from "../../directives/form-section-header-description/form-section-header-description.directive";

/**
 * Container component that wraps a subform and form list in a Material Card with a header.
 * Provides visual grouping and styling for related form sections.
 *
 * @example
 * <mgd-form-section>
 *   <span matListItemIcon mgdFormSectionIcon><mat-icon>distance</mat-icon></span>
 *   <span mgdFormSectionHeader>Subform Component</span>
 *   <span mgdFormSectionHeaderDescription>Optional description shown under the header</span>
 *   <mgd-subform #mgdSubform="mgdSubform" [formGroup]="subformGroup" (submitSubform)="onSubmitSubform($event)">
 *     <mat-form-field>
 *       <mat-label>Form Field 1</mat-label>
 *       <input matInput formControlName="subformField1" />
 *     </mat-form-field>
 *   </mgd-subform>
 *   <mgd-form-list formControlName="list" [subform]="mgdSubform">
 *     <mat-list-item mgdFormListItem="item" *mgdFormListData="let data; let i = index">
 *       <span>{{i}}: {{data.subformField1}}</span>
 *     </mat-list-item>
 *   </mgd-form-list>
 * </mgd-form-section>
 */
@Component({
    selector: "mgd-form-section",
    imports: [
        MatCard,
        MatCardHeader,
        MatCardTitle,
        CardContentDirective,
    ],
    templateUrl: "./form-section.component.html",
    styleUrl: "./form-section.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormSectionComponent implements AfterContentInit {

    /**
     * SubformComponent within the FormSectionComponent Content Children
     */
    @ContentChild(SubformComponent) subform?: SubformComponent;

    /**
     * FormSectionHeaderDescriptionDirective within the FormSectionComponent Content Children.
     * Presence drives whether the header's description wrapper is rendered at all.
     */
    @ContentChild(FormSectionHeaderDescriptionDirective) headerDescription?: FormSectionHeaderDescriptionDirective;

    private readonly _cdRef = inject(ChangeDetectorRef);

    private readonly _destroyRef = inject(DestroyRef);

    /**
     * The template reads `subform?.open` directly (a plain property the projected
     * SubformComponent mutates on itself, outside of this component's own Input/Output
     * flow). Under OnPush that mutation alone would never trigger a check here, so this
     * subscribes to the subform's `opened`/`closed` outputs - emitted at the same point
     * `open` changes - and marks this view for check when they fire.
     */
    ngAfterContentInit(): void {
        this.subform?.opened.pipe(
            takeUntilDestroyed(this._destroyRef)
        ).subscribe(() => this._cdRef.markForCheck());

        this.subform?.closed.pipe(
            takeUntilDestroyed(this._destroyRef)
        ).subscribe(() => this._cdRef.markForCheck());
    }

}
